<template>
  <div class="loansToRefinanceSection">
    <div class="loansToRefinanceTableWrapper">
      <div class="add-loan-btn-wrapper" v-if="showAddLoanButton">
        <button class="add-loan-btn add-btn" @click="handleAddLoanButtonClick">Add Loan</button>
        <p class="add-loan-disclaimer" v-show="addLoanClicked">
          If you want to add another loan, we will need to verify additional information. Please
          reach out to the processing team for assistance at
          <a class="email-link" :href="`mailto:${emailAddress}`">{{ emailAddress }}.</a>
        </p>
      </div>
      <div class="loans-to-refinance-section-table-header" v-if="showTableHeader">
        <div class="header title">
          <span>Loans to Refinance</span>
          <a class="edit-link" @click.prevent="openModal" v-if="canEdit" :disabled="!canEdit"
            >Edit</a
          >
        </div>
        <div class="header data" v-if="refiLoansData?.length > 0">
          Showing items {{ paginatedItemsDisplayCounter }} of {{ refiLoansData.length }} items
        </div>
      </div>
      <table class="loans-to-refinance-table">
        <thead>
          <tr>
            <th v-for="header in headers.filter((h: any) => h.visible)" :key="header.field">
              {{ header.label }}
            </th>
          </tr>
        </thead>
        <tbody>
          <template v-for="loan in paginatedItems" :key="loan.id">
            <tr>
              <td>{{ loan?.accountNumber }}</td>
              <td>{{ loan?.servicerName }}</td>
              <td>{{ loan?.creditReportBalanceDisplay }}</td>
              <td v-if="showPayoffDetails">{{ loan?.payoffAmountDisplay }}</td>
              <td v-if="showActionsColumn" class="actions">
                <label class="checkbox-container">
                  <input
                    class="form-check-input"
                    type="checkbox"
                    :value="loan.include"
                    :checked="loan.include"
                    @change.prevent="handleCheckboxEvent(loan, $event)"
                  />
                  <span class="checkmark"></span>
                </label>
              </td>
            </tr>
            <tr v-show="showPayoffDetails && loan?.payoffDetailDisplay">
              <td colspan="6">
                <div class="payoff-details-wrapper">
                  <div><i>Payoff Details</i></div>
                  <div>{{ loan?.payoffDetailDisplay }}</div>
                </div>
              </td>
            </tr>
          </template>
          <tr class="footer">
            <td class="text-right" colspan="6">
              <span>{{ tableFooterTotalText }} </span>
            </td>
          </tr>
          <tr class="footer inverse">
            <td class="text-right" colspan="6">
              {{ totalDisplay }}
            </td>
          </tr>
        </tbody>
      </table>
      <span>* = Optional if known</span>
      <div class="loans-to-refinance-table-footer row">
        <div class="pagination" v-if="paginationEnabled && pages.length > 1">
          <button
            class="pagination:number arrow"
            @click="previous"
            :disabled="pagination.currentPage === 1"
          >
            <span>&lt;</span>
          </button>
          <div class="pages-wrapper">
            <button
              class="page"
              v-for="page in pages"
              :key="page"
              :class="{ current: page === pagination.currentPage }"
              @click="changePage(page)"
            >
              {{ page }}
            </button>
          </div>
          <button
            class="pagination:number arrow"
            @click="next"
            :disabled="pagination.currentPage === pages.length"
          >
            <span>&gt;</span>
          </button>
        </div>
      </div>
    </div>
  </div>
</template>
<script setup lang="ts">
import { ref, reactive } from "vue";
import { useStore } from "vuex";
import { defineProps, computed } from "vue";
import useFormatter from "@/use/formatter";
import { ApplicationStatusTypeIds } from "@/config/constants";

const props = defineProps({
  id: {
    type: String,
    default: "",
  },
  showActionsColumn: {
    type: Boolean,
    default: false,
  },
  showPayoffDetails: {
    type: Boolean,
    default: false,
  },
  showTableHeader: {
    type: Boolean,
    default: true,
  },
  showAddLoanButton: {
    type: Boolean,
    default: true,
  },
  showIncludedLoansOnly: {
    type: Boolean,
    default: false,
  },
});

const store = useStore();
const { formatMoney } = useFormatter();

const loading = ref<boolean>(true);
const addLoanClicked = ref<boolean>(false);

const applicationStatusId = computed(() => store.state.appData?.application?.applicationStatusId);

const emailAddress = computed(() => store.getters["contactUs"]?.email);
const refiLoans = computed(() => store?.getters["applicationInfoModule/RefiLoans"]);
const filteredRefiLoans = computed(
  () => store?.getters["applicationInfoModule/FilteredRefiLoans"] || [],
);

const canEdit = computed(
  () =>
    (applicationStatusId.value === ApplicationStatusTypeIds.SUBMITTED_AND_PROCESSING &&
      store.getters["consentsModule/SLR_PAYOFF_CONFIRMATION_EXISTS"] === false) ||
    (applicationStatusId.value !== ApplicationStatusTypeIds.RIGHT_TO_CANCEL &&
      applicationStatusId.value !== ApplicationStatusTypeIds.COMPLETE),
);

const loanDetailsModal = computed(() => store.state.applicationInfoModule.loanModal);

const refiLoansData = computed(() => {
  switch (props.id) {
    case "ApplicationInfoTile":
      return refiLoans.value;
    case "Parent_LoanDetailsModal":
      return loanDetailsModal.value.loans;
    case "Parent_ConfirmYourRefinanceDetailsModal":
      return loanDetailsModal.value.loans;
    default:
      return [];
  }
});

const tableFooterTotalText = computed(() => {
  switch (props.id) {
    case "ApplicationInfoTile":
      return "Estimated Total To Refinance";
    default:
      return "Total to Refinance";
  }
});

const totalDisplay = computed(() => {
  switch (props.id) {
    case "ApplicationInfoTile":
      return estimatedTotalToRefinanceDisplay.value;
    case "Parent_ConfirmYourRefinanceDetailsModal":
      return loanDetailsModal.value.totalToRefinanceDisplay;
    default:
      return calculatedEstimatedTotalToRefinanceDisplay.value;
  }
});

const calculatedEstimatedTotalToRefinance = computed<number>(() => {
  // only include the candidate refi loans that have include flag set to true
  // manually added candidate refi loans should be included by default since ui does not show a checkbox
  return refiLoansData.value?.reduce(function (acc: any, obj: any) {
    let bal =
      typeof obj.creditReportBalance === "string"
        ? parseFloat(obj.creditReportBalance)
        : obj.creditReportBalance;

    return obj.include ? acc + bal : acc;
  }, 0);
});
const calculatedEstimatedTotalToRefinanceDisplay = computed<number>(() => {
  return formatMoney(calculatedEstimatedTotalToRefinance.value, 0);
});
const estimatedTotalToRefinanceDisplay = computed(
  () => store?.state?.applicationInfoModule?.applicationInfo?.estimatedTotalDisplay,
);

const headers = computed(() => [
  { label: "Account Number* (if known)", field: "accountNumber", visible: true },
  { label: "Lender/Loan Servicer", field: "servicerName", visible: true },
  { label: "Estimated Current Balance", field: "creditReportBalanceDisplay", visible: true },
  { label: "Final Payoff Amount", field: "payoffAmountDisplay", visible: props.showPayoffDetails },
  { label: "Include?", field: "", visible: props.showActionsColumn },
]);

function openModal() {
  store.dispatch("applicationInfoModule/openModal", "LoanDetails");
}

function handleCheckboxEvent(refiLoan: any, event: any) {
  const updatedRefiLoan = Object.assign({}, refiLoan);
  updatedRefiLoan.include = event.target.checked;
  store.dispatch("applicationInfoModule/updateRefiLoan", updatedRefiLoan);
  store.dispatch(
    "applicationInfoModule/setCalculatedEstimatedTotalToRefinance",
    calculatedEstimatedTotalToRefinance.value,
  );
}

function handleAddLoanButtonClick() {
  addLoanClicked.value = true;
}

// PAGINATION
const paginationEnabled = computed<boolean>(() => refiLoansData.value?.length > 3);

const pagination = reactive({
  currentPage: 1,
  allowPagination: false,
  perPage: computed(() =>
    pagination.allowPagination || refiLoansData.value?.length <= 0
      ? 3
      : refiLoansData.value?.length,
  ),
  totalPages: computed(() => Math.ceil(refiLoansData.value?.length / pagination.perPage)),
  pages: [],
}) as any;

const pages = computed<Array<any>>((i: any) => {
  return [...Array(pagination.totalPages).keys()].map(i => i + 1);
});

const paginatedItems = computed(() => {
  const { currentPage, perPage } = pagination;

  if (!currentPage || !paginationEnabled.value) {
    return refiLoansData.value;
  } else {
    const start = (currentPage - 1) * perPage;
    const stop = start + perPage;
    return refiLoansData.value.slice(start, stop);
  }
});

const starting = computed<number>(() =>
  Math.ceil((pagination.currentPage - 1) * pagination.perPage),
);
const ending = computed<number>(() =>
  starting.value + 1 * pagination.perPage > refiLoansData.value.length
    ? refiLoansData.value.length
    : starting.value + 1 * pagination.perPage,
);
const paginatedItemsDisplayCounter = computed(() =>
  paginationEnabled.value
    ? `${starting.value + 1} - ${ending.value} `
    : `${starting.value + 1} - ${refiLoansData.value.length} `,
);
function previous() {
  if (pagination.currentPage > 1) {
    pagination.currentPage--;
    loading.value = true;
  }
}
function next() {
  if (pagination.currentPage < pagination.totalPages) {
    window.scrollTo(0, 0);
    pagination.currentPage++;
    loading.value = true;
  }
}
function changePage(newPage: string) {
  window.scrollTo(0, 0);
  pagination.currentPage = parseInt(newPage);
  loading.value = true;
}
</script>
<style scoped lang="scss">
/* Anchor Styling */

/* Button Styling */
.add-loan-btn-wrapper {
  text-align: right;
  margin-bottom: 1em;
}
.add-loan-btn {
  width: 74px;
  height: 28px;
  border-radius: 4px;
  border: 1px solid var(--theme-color);
  background-color: var(--theme-color);
  color: #ffffff;
  font-family: var(--body-font);
  font-size: 1rem;
  font-weight: 600;
  line-height: 20px;
  text-align: center;
  cursor: pointer;
}
.add-loan-btn.add-btn {
  width: 91px;
}

.add-loan-disclaimer {
  color: #8b0e04;
  text-align: left;
}
.add-loan-disclaimer a {
  color: #8b0e04;
  text-decoration: underline;
  cursor: pointer;
}

/* Table Header Styling */
.loans-to-refinance-section-table-header {
  font-weight: 700;
  display: flex;
  justify-content: space-between;
}
.loans-to-refinance-section-table-header .header.title {
  display: flex;
  gap: 1em;
  align-items: center;
}
.loans-to-refinance-section-table-header .header.data {
  font-weight: 400;
}
.edit-link {
  color: #767676;
  font-size: 0.8em;
  font-weight: 400;
  margin: 0;
  text-align: end;
  text-decoration: underline;
  cursor: pointer;
}

/* Table Styling */
.loans-to-refinance-table {
  font-family: var(--body-font);
  font-weight: 400;
  background: #fff;
  width: 100%;
  color: #333333;
  border-collapse: collapse;
}
.loans-to-refinance-table thead tr th {
  font-family: var(--body-font);
  font-size: 14px;
  font-weight: 600;
  line-height: 19.6px;
  text-align: left;
  border: 1px solid #ffffff;
  background: var(--theme-color);
  color: #ffffff;
}
.loans-to-refinance-table thead tr th:first {
  border-left: 1px solid var(--theme-color);
}
.loans-to-refinance-table thead tr th:last-of-type {
  border-right: 1px solid var(--theme-color);
}
.loans-to-refinance-table thead tr,
.loans-to-refinance-table thead th,
.loans-to-refinance-table tbody tr:not(.footer),
.loans-to-refinance-table tbody td:not(.text-right),
.loans-to-refinance-table tbody td:not(.actions) {
  text-align: left;
  padding: 8px;
}
tr,
tbody tr td:not(.actions) {
  border: 1px solid var(--theme-color);
}
td.actions {
  display: flex;
  justify-content: center;
  border: 1px solid transparent;
}
tr.footer {
  background-color: var(--theme-color);
  color: #ffffff;
  font-weight: 700;
}
tr.no-border {
  border: none;
}
tr.footer.inverse {
  color: #000000;
  background-color: #ffffff;
  font-weight: 700;
}
tr.footer td {
  text-align: right;
  padding: 8px;
}
.loansToRefinanceSection {
  display: flex;
  flex-direction: column;
  gap: 1em;
}

.loans-to-refinance-table-footer.row {
  align-items: flex-start;
  justify-content: flex-end;
}

/* Pagination Styling */
.pagination {
  display: flex;
  margin-top: 0.875rem;
}
.pagination .pages-wrapper {
  display: flex;
  align-items: center;
  gap: 0.25em;
}
.pagination\:number {
  --margin: 6px;
  margin: 0 var(--margin);
  background-color: var(--cuscd-refi-white);
  color: var(--theme-color);
}
.pagination\:number.arrow {
  font-weight: 700;
}
.pagination button {
  border: 1px solid #bebebe;
  border-radius: 4px;
  background-color: #ffffff;
  border: 1px solid #bebebe;
  cursor: pointer;
}
.pagination button:disabled {
  color: #bebebe;
  border: 1px solid #bebebe;
  cursor: not-allowed;
}
.pagination .pages-wrapper button.current {
  background-color: var(--theme-color);
  color: #ffffff;
  border: 1px solid var(--theme-color);
}

/* Checkbox Styling */
.checkbox-container {
  font-family: var(--body-font);
  font-weight: 400;
  display: block;
  position: relative;
  padding-left: 24px;
  margin-bottom: 0px;
  cursor: pointer;
  -webkit-user-select: none;
  -moz-user-select: none;
  -ms-user-select: none;
  user-select: none;

  &[disabled="true"] {
    cursor: not-allowed;

    .checkmark {
      background-color: #e5e4e2;
    }
  }

  input {
    position: absolute;
    opacity: 0;
    cursor: pointer;
    height: 0;
    width: 0;

    &:checked ~ .checkmark {
      background-color: var(--theme-color);

      &:after {
        display: block;
      }
    }
  }

  .checkmark {
    position: absolute;
    top: 2px;
    left: 0;
    height: 20px;
    width: 20px;
    background-color: #fff;
    border: 1px solid #ced4da;
    border-radius: 4px;

    &:after {
      content: "";
      position: absolute;
      display: none;
    }
  }

  .checkmark:after {
    left: 5px;
    top: 2px;
    width: 8px;
    height: 12px;
    border: solid white;
    border-width: 0 3px 3px 0;
    -webkit-transform: rotate(45deg);
    -ms-transform: rotate(45deg);
    transform: rotate(45deg);
  }
}

.payoff-details-wrapper {
  display: flex;
  flex-direction: column;
  flex-wrap: wrap;
  align-content: center;
  text-align: center;
}

// Small and medium devices (landscape phones,tablets 992px and down)
@media (max-width: 992px) {
  .edit-link {
    font-size: max(16px, 1em);
  }
  .checkbox-container {
  }
}

@media (max-width: 575px) {
  .loansToRefinanceTableWrapper {
    overflow-x: auto;
    overflow-y: auto;
  }
  .loans-to-refinance-section-table-header {
    flex-direction: column;
  }

  .loans-to-refinance-section-table-header .header.data {
    text-align: end;
  }
}
</style>
